import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Building2,
  Plus,
  Key,
  Mail,
  RefreshCw,
  Copy,
  Check,
  X,
  Eye,
  EyeOff,
  Users,
  ShoppingCart,
  TrendingUp,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function CashXAdmin() {
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [showKeys, setShowKeys] = useState({});
  
  // New account form
  const [newAccount, setNewAccount] = useState({
    business_name: '',
    owner_email: '',
    owner_password: '',
    terminal_name: 'Hauptkasse'
  });
  const [createLoading, setCreateLoading] = useState(false);

  // Stats
  const [stats, setStats] = useState({ total_accounts: 0, active_accounts: 0, total_orders: 0 });

  // Fetch accounts
  const fetchAccounts = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/cashx/accounts`);
      if (response.ok) {
        const data = await response.json();
        setAccounts(data.accounts);
        setStats({
          total_accounts: data.count,
          active_accounts: data.accounts.filter(a => a.active).length,
          total_orders: 0 // Would need a separate API call
        });
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Accounts');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Create account
  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/cashx/accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_name: newAccount.business_name,
          owner_email: newAccount.owner_email,
          owner_password: newAccount.owner_password,
          settings: {
            terminal_name: newAccount.terminal_name,
            notification_sound: true
          }
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Account "${data.business_name}" erstellt!`);
        setShowCreateDialog(false);
        setNewAccount({ business_name: '', owner_email: '', owner_password: '', terminal_name: 'Hauptkasse' });
        fetchAccounts();
      } else {
        toast.error(data.detail?.error || 'Fehler beim Erstellen');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    } finally {
      setCreateLoading(false);
    }
  };

  // Toggle account active
  const toggleAccountActive = async (accountId, currentActive) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/cashx/accounts/${accountId}?active=${!currentActive}`,
        { method: 'PATCH' }
      );
      
      if (response.ok) {
        toast.success(currentActive ? 'Account deaktiviert' : 'Account aktiviert');
        fetchAccounts();
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  // Regenerate API key
  const regenerateApiKey = async (accountId) => {
    if (!window.confirm('Neuen API-Key generieren? Der alte Key wird ungültig!')) return;
    
    try {
      const response = await fetch(
        `${API_BASE}/api/cashx/accounts/${accountId}?regenerate_key=true`,
        { method: 'PATCH' }
      );
      
      if (response.ok) {
        toast.success('Neuer API-Key generiert');
        fetchAccounts();
      }
    } catch (error) {
      toast.error('Fehler beim Generieren');
    }
  };

  // Copy to clipboard
  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(id);
      toast.success('In Zwischenablage kopiert');
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (error) {
      toast.error('Kopieren fehlgeschlagen');
    }
  };

  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white p-6" data-testid="cashx-admin-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">
              <span className="text-amber-400">Cash-X</span>
              <span className="text-gray-400 ml-2">Admin</span>
            </h1>
            <p className="text-gray-500 mt-1">Account-Verwaltung für Multi-Tenant System</p>
          </div>
          
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={fetchAccounts}
              className="border-gray-700"
            >
              <RefreshCw size={18} className="mr-2" />
              Aktualisieren
            </Button>
            
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="bg-amber-500 hover:bg-amber-600 text-black">
                  <Plus size={18} className="mr-2" />
                  Neuer Account
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-gray-900 border-gray-800">
                <DialogHeader>
                  <DialogTitle className="text-white">Neuen Account erstellen</DialogTitle>
                  <DialogDescription className="text-gray-400">
                    Erstellen Sie einen neuen Cash-X Account für ein Restaurant.
                  </DialogDescription>
                </DialogHeader>
                
                <form onSubmit={handleCreateAccount} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Firmenname</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                      <Input
                        placeholder="z.B. ZOZO Burger Rellingen"
                        value={newAccount.business_name}
                        onChange={(e) => setNewAccount({...newAccount, business_name: e.target.value})}
                        className="pl-10 bg-gray-800 border-gray-700 text-white"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-gray-300">E-Mail</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                      <Input
                        type="email"
                        placeholder="info@beispiel.de"
                        value={newAccount.owner_email}
                        onChange={(e) => setNewAccount({...newAccount, owner_email: e.target.value})}
                        className="pl-10 bg-gray-800 border-gray-700 text-white"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-gray-300">Passwort</Label>
                    <div className="relative">
                      <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                      <Input
                        type="password"
                        placeholder="Mindestens 6 Zeichen"
                        value={newAccount.owner_password}
                        onChange={(e) => setNewAccount({...newAccount, owner_password: e.target.value})}
                        className="pl-10 bg-gray-800 border-gray-700 text-white"
                        required
                        minLength={6}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-gray-300">Kassenname</Label>
                    <Input
                      placeholder="z.B. Hauptkasse"
                      value={newAccount.terminal_name}
                      onChange={(e) => setNewAccount({...newAccount, terminal_name: e.target.value})}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  
                  <DialogFooter className="pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowCreateDialog(false)}
                      className="border-gray-700"
                    >
                      Abbrechen
                    </Button>
                    <Button
                      type="submit"
                      className="bg-amber-500 hover:bg-amber-600 text-black"
                      disabled={createLoading}
                    >
                      {createLoading ? <RefreshCw className="animate-spin mr-2" size={18} /> : <Plus size={18} className="mr-2" />}
                      Erstellen
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Gesamt Accounts</p>
                  <p className="text-3xl font-bold text-white">{stats.total_accounts}</p>
                </div>
                <div className="p-3 rounded-full bg-blue-500/10">
                  <Users className="text-blue-400" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Aktive Accounts</p>
                  <p className="text-3xl font-bold text-green-400">{stats.active_accounts}</p>
                </div>
                <div className="p-3 rounded-full bg-green-500/10">
                  <Check className="text-green-400" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Inaktive Accounts</p>
                  <p className="text-3xl font-bold text-red-400">{stats.total_accounts - stats.active_accounts}</p>
                </div>
                <div className="p-3 rounded-full bg-red-500/10">
                  <X className="text-red-400" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Accounts Table */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Building2 size={20} />
              Accounts ({accounts.length})
            </CardTitle>
            <CardDescription className="text-gray-400">
              Alle registrierten Cash-X Accounts
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <RefreshCw className="animate-spin text-gray-400" size={32} />
              </div>
            ) : accounts.length === 0 ? (
              <div className="text-center py-12">
                <Building2 size={48} className="mx-auto text-gray-600 mb-4" />
                <p className="text-gray-400">Noch keine Accounts vorhanden</p>
                <Button
                  className="mt-4 bg-amber-500 hover:bg-amber-600 text-black"
                  onClick={() => setShowCreateDialog(true)}
                >
                  <Plus size={18} className="mr-2" />
                  Ersten Account erstellen
                </Button>
              </div>
            ) : (
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-800">
                      <TableHead className="text-gray-400">Account</TableHead>
                      <TableHead className="text-gray-400">E-Mail</TableHead>
                      <TableHead className="text-gray-400">API-Key</TableHead>
                      <TableHead className="text-gray-400">Status</TableHead>
                      <TableHead className="text-gray-400">Erstellt</TableHead>
                      <TableHead className="text-gray-400 text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {accounts.map((account) => (
                      <TableRow key={account.account_id} className="border-gray-800">
                        <TableCell>
                          <div>
                            <p className="font-medium text-white">{account.business_name}</p>
                            <p className="text-xs text-gray-500">{account.account_id}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-300">{account.owner_email}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <code className="text-xs bg-gray-800 px-2 py-1 rounded text-amber-400 font-mono">
                              {showKeys[account.account_id] 
                                ? account.api_key 
                                : account.api_key.substring(0, 12) + '•••••••••'}
                            </code>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-gray-400"
                              onClick={() => setShowKeys({...showKeys, [account.account_id]: !showKeys[account.account_id]})}
                            >
                              {showKeys[account.account_id] ? <EyeOff size={14} /> : <Eye size={14} />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-gray-400"
                              onClick={() => copyToClipboard(account.api_key, account.account_id)}
                            >
                              {copiedKey === account.account_id ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={account.active ? 'default' : 'secondary'} className={account.active ? 'bg-green-500' : 'bg-red-500'}>
                            {account.active ? 'Aktiv' : 'Inaktiv'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-400 text-sm">
                          {formatDate(account.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-gray-700 text-xs"
                              onClick={() => regenerateApiKey(account.account_id)}
                            >
                              <RefreshCw size={14} className="mr-1" />
                              Neuer Key
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className={account.active ? 'border-red-500/50 text-red-400' : 'border-green-500/50 text-green-400'}
                              onClick={() => toggleAccountActive(account.account_id, account.active)}
                            >
                              {account.active ? 'Deaktivieren' : 'Aktivieren'}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
        
        {/* API Documentation */}
        <Card className="bg-gray-900 border-gray-800 mt-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Key size={20} />
              API Integration
            </CardTitle>
            <CardDescription className="text-gray-400">
              So integrieren Sie Cash-X in Ihr Bestellsystem
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-gray-800">
                <p className="text-sm text-gray-400 mb-2">Bestellung senden:</p>
                <pre className="text-xs text-amber-400 font-mono overflow-x-auto">
{`curl -X POST ${window.location.origin}/api/cashx/orders \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: <IHR_API_KEY>" \\
  -d '{
    "order_number": "ZOZO-1234",
    "items": [{"name": "Burger", "quantity": 1, "price": 9.99}],
    "total": 9.99,
    "payment_method": "cash",
    "customer": {"name": "Max", "phone": "0171..."},
    "delivery_type": "pickup"
  }'`}
                </pre>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-gray-800">
                  <p className="text-sm text-gray-400 mb-2">Kassen-URL:</p>
                  <code className="text-amber-400 font-mono">{window.location.origin}/kasse</code>
                </div>
                <div className="p-4 rounded-lg bg-gray-800">
                  <p className="text-sm text-gray-400 mb-2">Health Check:</p>
                  <code className="text-amber-400 font-mono">{window.location.origin}/api/cashx/health</code>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
